import { FunctionComponent, ReactNode, useEffect, useState } from 'react'
import { Parent } from '@/types/Parent'
import styles from './familiesTableHandler.module.scss'
import { useTranslation } from 'react-i18next'
import { Daycare, DaycareType } from '@/types/Daycare'
import Indicator from '@/components/_custom/Indicator/indicator'
import Title from '@/components/_custom/Title/title'
import { frFR } from '@mui/x-data-grid/locales'
import { QuickSearchToolbar, StyledDataGrid } from '@/components/_custom/DataGrid'
import { GridColDef } from '@mui/x-data-grid'
import { Add, ChildCare, Close, ErrorOutline, LinkOff, Settings } from '@mui/icons-material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import Desktop from '@/modules/desktopMobile/desktop'
import Mobile from '@/modules/desktopMobile/mobile'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import AddFamilyDialog from '../header/Dialog/addFamilyDialog'
import { Child, ChildDaycare } from '@/types/Child'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { useAuth } from '@/modules/context/AuthContext'
import { Role } from '@/types/Role'
import ChildListDialog from './Dialog/childListDialog'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'
import BulkDissociateParentsDialog from './Dialog/bulkDissociateFamiliesDialog'

interface familiesTableHandlerProps {
    families: Array<Parent>
    currentDaycare?: Daycare
    refetchAllParent: () => void
}

type DataTable = {
    id: number
    childCount: number
    childsName: string
    childsList: Child[]
    containWithoutSection: boolean
    lastname: string
    email: string
    phone: string
}

const FamiliesTableHandler: FunctionComponent<familiesTableHandlerProps> = ({
    families,
    currentDaycare,
    refetchAllParent
}: familiesTableHandlerProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const { showNotificationSuccess } = useNotification()

    const navigate: NavigateFunction = useNavigate()

    const [countFamilies, setCountFamilies] = useState<number>(0)
    const [countChild, setCountChild] = useState<number>(0)
    const [countWithoutSections, setCountWithoutSections] = useState<number>(0)
    const [tableData, setTableData] = useState<DataTable[]>([])
    const [selectedParentIds, setSelectedParentIds] = useState<number[]>([])
    const [isBulkDissociateDialogOpen, setBulkDissociateDialogOpen] = useState(false)

    function toggleParent(parentId: number): void {
        setSelectedParentIds((prev: number[]): number[] =>
            prev.includes(parentId) ? prev.filter((id: number): boolean => id !== parentId) : [...prev, parentId]
        )
    }

    function toggleAllParents(select: boolean): void {
        setSelectedParentIds(select ? families.map((parent: Parent): number => parent.id) : [])
    }

    const [isAddFamilyDialogOpen, setAddFamilyDialogOpen] = useState<boolean>(false)

    const [autoLinkDaycare, setAutoLinkDaycare] = useState<string>('')
    const [autoLinkActive, setAutoLinkActive] = useState<boolean>(false)

    const [isChildDialogOpen, setChildDialogOpen] = useState(false)
    const [childs, setChilds] = useState<Child[]>([])

    useEffect(() => {
        setAutoLinkDaycare(currentDaycare?.autoLink || '')
        setAutoLinkActive(!!currentDaycare?.autoLink)
    }, [currentDaycare])

    useEffect(() => {
        if (families.length > 0) {
            setCountFamilies(getCountFamilies(families))
            setCountChild(getCountChildren(families))
            setCountWithoutSections(getWithoutSections(families))
            setTableData(getTableData(families))
        } else {
            setCountFamilies(0)
            setCountChild(0)
            setCountWithoutSections(0)
            setTableData([])
        }
    }, [families])

    function getCountChildren(families: Parent[]): number {
        let count: number = 0
        for (let i: number = 0; i < families.length; i++) {
            if (families[i].id === 0) continue

            count += families[i].childs.length
        }
        return count
    }

    function getCountFamilies(families: Parent[]): number {
        let count: number = 0
        for (let i: number = 0; i < families.length; i++) {
            if (families[i].id === 0) continue

            count += 1
        }
        return count
    }

    function getWithoutSections(families: Parent[]): number {
        let count: number = 0
        for (let i: number = 0; i < families.length; i++) {
            if (families[i].id === 0) continue

            for (let y: number = 0; y < families[i].childs.length; y++) {
                if (
                    families[i].childs[y].daycares.find((daycare) => {
                        return (
                            daycare.daycareId === Number(currentDaycare?.id) &&
                            daycare.visible &&
                            daycare.sections.length === 0
                        )
                    })
                ) {
                    count += 1
                }
            }
        }
        return count
    }

    function getTableData(families: Parent[]): DataTable[] {
        const tableData: DataTable[] = []
        for (let i: number = 0; i < families.length; i++) {
            if (families[i].id === 0) continue

            let containWithoutSection: boolean = false

            families[i].childs.map((child: Child): void => {
                child.daycares.map((daycare: ChildDaycare): void => {
                    if (
                        daycare.daycareId === Number(currentDaycare?.id) &&
                        daycare.visible &&
                        daycare.sections.length === 0
                    ) {
                        containWithoutSection = true
                    }
                })
            })

            tableData.push({
                id: families[i].id,
                childCount: families[i].childs.length,
                childsName: families[i].childs.map((child: Child) => child.firstname).join(', '),
                childsList: families[i].childs,
                containWithoutSection: containWithoutSection,
                lastname: families[i].lastname,
                email: families[i].email,
                phone: families[i].phone
            })
        }
        return tableData.sort((a, b) => a.lastname.localeCompare(b.lastname))
    }

    const isRpeOrLaep: boolean = currentDaycare?.type === DaycareType.rpe || currentDaycare?.type === DaycareType.laep

    const headerClassName: string = user?.role === Role.parent ? styles.HeaderParent : styles.HeaderDaycare

    const columnsWeb: GridColDef[] = [
        {
            field: 'select',
            headerClassName: headerClassName,
            width: 20,
            align: 'center',
            sortable: false,
            renderHeader: () => (
                <div className={styles.IconContainer}>
                    <Checkbox
                        checked={selectedParentIds.length > 0 && selectedParentIds.length === families.length}
                        indeterminate={selectedParentIds.length > 0 && selectedParentIds.length < families.length}
                        onChange={toggleAllParents}
                        className={styles.HeaderCheckbox}
                    />
                </div>
            ),
            renderCell: (params) => (
                <div className={styles.IconContainer}>
                    <Checkbox
                        className={styles.Checkbox}
                        checked={selectedParentIds.includes(params.row.id)}
                        onChange={() => toggleParent(params.row.id)}
                    />
                </div>
            )
        },
        {
            field: 'childCount',
            headerName: t('childCount'),
            headerClassName: headerClassName,
            width: 100,
            maxWidth: 200,
            minWidth: 100,
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.IconContainer} onClick={() => openChildList(params.row.childsList)}>
                    {params?.row.childCount ? params.row.childCount : 0}
                    <ChildCare className={styles.Icon} />
                </div>
            )
        },
        {
            field: 'childsName',
            headerName: t('childs'),
            headerClassName: headerClassName,
            width: 1,
            minWidth: 1,
            maxWidth: 1,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.Hide}>{params.row.childsName ? params.row.childsName : ''}</div>
            )
        },
        {
            field: 'containWithoutSection',
            headerName: '',
            headerClassName: headerClassName,
            width: 80,
            maxWidth: 80,
            minWidth: 80,
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <div
                    className={`${styles.IconContainer} ${styles.Clickable}`}
                    onClick={() => navigate(`/adults?parent=${params.row.id}`)}
                >
                    {params?.row.containWithoutSection ? <ErrorOutline /> : ''}
                </div>
            )
        },
        {
            field: 'lastname',
            headerName: t('lastname'),
            headerClassName: headerClassName,
            width: 200,
            minWidth: 200,
            maxWidth: 400,
            flex: 1,
            renderCell: (params) => (
                <a
                    href={`/adults?parent=${params.row.id}`}
                    onClick={(e) => {
                        if (!(e.ctrlKey || e.metaKey)) {
                            e.preventDefault()
                            navigate(`/adults?parent=${params.row.id}`)
                        }
                    }}
                    className={`${styles.InfoContainer} ${styles.Clickable}`}
                >
                    {params.row.lastname}
                </a>
            )
        },
        {
            field: 'email',
            headerName: t('email'),
            headerClassName: headerClassName,
            width: 200,
            maxWidth: 400,
            minWidth: 200,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.email ? (
                        <a href={`mailto:${params.row.email}`}>{params.row.email}</a>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'phone',
            headerName: t('phone'),
            headerClassName: headerClassName,
            width: 150,
            maxWidth: 400,
            minWidth: 100,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.InfoContainer}>
                    {params.row.phone ? (
                        <a href={`tel:${params.row.phone}`}>{params.row.phone.match(/.{1,2}/g).join(' ')}</a>
                    ) : (
                        <Close className={styles.RedIcon} />
                    )}
                </div>
            )
        },
        {
            field: 'actions',
            headerName: t('actions'),
            headerClassName: headerClassName,
            width: 200,
            maxWidth: 300,
            minWidth: 100,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.IconContainer}>
                    <a
                        href={`/adults?parent=${params.row.id}`}
                        onClick={(e) => {
                            if (!(e.ctrlKey || e.metaKey)) {
                                e.preventDefault()
                                navigate(`/adults?parent=${params.row.id}`)
                            }
                        }}
                        className={styles.Link}
                    >
                        <VisibilityIcon className={styles.Icon} />
                    </a>
                </div>
            )
        }
    ]

    const columnsMobile: GridColDef[] = [
        {
            field: 'select',
            headerClassName: headerClassName,
            width: 20,
            align: 'center',
            sortable: false,
            renderHeader: () => (
                <div className={styles.IconContainer}>
                    <Checkbox
                        checked={selectedParentIds.length > 0 && selectedParentIds.length === families.length}
                        indeterminate={selectedParentIds.length > 0 && selectedParentIds.length < families.length}
                        onChange={toggleAllParents}
                        className={styles.HeaderCheckbox}
                    />
                </div>
            ),
            renderCell: (params) => (
                <div className={styles.IconContainer}>
                    <Checkbox
                        className={styles.Checkbox}
                        checked={selectedParentIds.includes(params.row.id)}
                        onChange={() => toggleParent(params.row.id)}
                    />
                </div>
            )
        },
        {
            field: 'childCount',
            headerName: t('childs'),
            headerClassName: headerClassName,
            width: 120,
            maxWidth: 200,
            minWidth: 50,
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.IconContainer} onClick={() => openChildList(params.row.childsList)}>
                    {params?.row.childCount ? params.row.childCount : 0}
                    <ChildCare className={styles.Icon} />
                </div>
            )
        },
        {
            field: 'containWithoutSection',
            headerName: '',
            headerClassName: headerClassName,
            width: 30,
            maxWidth: 50,
            minWidth: 30,
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <div
                    className={`${styles.IconContainer} ${styles.Clickable}`}
                    onClick={() => navigate(`/adults?parent=${params.row.id}`)}
                >
                    {params?.row.containWithoutSection ? <ErrorOutline /> : ''}
                </div>
            )
        },
        {
            field: 'childsName',
            headerName: t('childs'),
            headerClassName: headerClassName,
            width: 1,
            minWidth: 1,
            maxWidth: 1,
            flex: 1,
            renderCell: (params) => (
                <div className={styles.Hide}>{params.row.childsName ? params.row.childsName : ''}</div>
            )
        },
        {
            field: 'lastname',
            headerName: t('lastname'),
            headerClassName: headerClassName,
            width: 100,
            minWidth: 100,
            maxWidth: 250,
            flex: 1,
            renderCell: (params) => (
                <div
                    className={`${styles.InfoContainer} ${styles.Clickable}`}
                    onClick={() => navigate(`/adults?parent=${params.row.id}`)}
                >
                    {params.row.lastname}
                </div>
            )
        },
        {
            field: 'actions',
            headerName: t('actions'),
            headerClassName: headerClassName,
            width: 100,
            maxWidth: 250,
            minWidth: 120,
            flex: 1,
            align: 'center',
            sortable: false,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className={styles.IconContainer}>
                    <VisibilityIcon
                        onClick={() => navigate(`/adults?parent=${params.row.id}`)}
                        className={styles.Icon}
                    />
                </div>
            )
        }
    ]

    function openChildList(childs: Child[]): void {
        if (childs && childs.length > 0) {
            setChilds(childs)
            setChildDialogOpen(true)
        } else {
            showNotificationSuccess(t('noChilds'))
        }
    }

    function filterWithoutSection(): void {
        setTableData(getTableData(families).filter((row) => row.containWithoutSection))
    }

    function filterChilds(): void {
        setTableData(getTableData(families).filter((row) => row.childCount > 0))
    }

    function resetFilter(): void {
        setTableData(getTableData(families))
    }

    const toolbarActions: ReactNode =
        selectedParentIds.length > 0 && currentDaycare && user?.currentDaycareRights.canWriteFamilies ? (
            <div className={styles.BulkActions}>
                <p className={styles.SelectedCount}>{t('parentsSelected', { count: selectedParentIds.length })}</p>
                <ButtonTextIcon
                    type={'custom'}
                    onClick={() => setBulkDissociateDialogOpen(true)}
                    customIcon={<LinkOff />}
                    customText={t('dissociateSelectedFamilies')}
                />
            </div>
        ) : null

    return (
        <>
            <div className={styles.IndicatorsContainer}>
                <a onClick={resetFilter} className={styles.Clickable}>
                    <Indicator
                        count={countFamilies}
                        backgroundWhite
                        text={
                            isRpeOrLaep
                                ? countFamilies > 1
                                    ? t('publics')
                                    : t('public')
                                : countFamilies > 1
                                  ? t('families')
                                  : t('family')
                        }
                    />
                </a>
                <a onClick={filterChilds} className={styles.Clickable}>
                    <Indicator count={countChild} backgroundWhite text={countChild > 1 ? t('childs') : t('child')} />
                </a>
                <Desktop>
                    <a onClick={filterWithoutSection} className={styles.Clickable}>
                        <Indicator
                            count={countWithoutSections}
                            backgroundWhite
                            text={isRpeOrLaep ? t('withoutSector') : t('withoutSection')}
                        />
                    </a>
                </Desktop>
            </div>
            {user?.currentDaycareRights.canWriteFamilies && (
                <Mobile>
                    <div className={styles.Parameters}>
                        <ButtonTextIcon
                            type={'custom'}
                            onClick={() => setAddFamilyDialogOpen(true)}
                            customIcon={<Add />}
                            customText={isRpeOrLaep ? t('invitePublic') : t('inviteFamily')}
                        />
                        <Settings
                            className={styles.Icon}
                            onClick={() => navigate(`/daycare?daycare=${currentDaycare?.id}#settings`)}
                        />
                        <AddFamilyDialog
                            open={isAddFamilyDialogOpen}
                            closeDialog={() => setAddFamilyDialogOpen(false)}
                            daycare={currentDaycare}
                            isRpeOrLaep={isRpeOrLaep}
                            autoLinkActive={autoLinkActive}
                            autoLinkDaycare={autoLinkDaycare}
                            refetchAllParent={refetchAllParent}
                            refetchAllChilds={refetchAllParent}
                        />
                    </div>
                </Mobile>
            )}
            <div className={styles.DataGridContainer}>
                <div className={styles.Title}>
                    <Title text={isRpeOrLaep ? t('myPublics') : t('myFamilies')} />
                </div>
                <Desktop>
                    <StyledDataGrid
                        autoHeight
                        columns={columnsWeb}
                        rows={tableData}
                        disableDensitySelector
                        disableColumnMenu
                        slots={{ toolbar: QuickSearchToolbar }}
                        slotProps={{ toolbar: { actions: toolbarActions } }}
                        showToolbar
                        sx={{ '--DataGrid-overlayHeight': '300px' }}
                        localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                        rowHeight={80}
                        className={styles.DataGrid}
                        rowCount={tableData.length}
                        disableRowSelectionOnClick
                        paginationMode={'server'}
                        initialState={{
                            filter: {
                                filterModel: {
                                    items: [],
                                    quickFilterExcludeHiddenColumns: false
                                }
                            }
                        }}
                        columnVisibilityModel={{
                            childsName: false
                        }}
                        onRowDoubleClick={(params) => {
                            navigate(`/adults?parent=${params.row.id}`)
                        }}
                    />
                </Desktop>
                <Mobile>
                    <StyledDataGrid
                        autoHeight
                        columns={columnsMobile}
                        rows={tableData}
                        disableDensitySelector
                        disableColumnMenu
                        slots={{ toolbar: QuickSearchToolbar }}
                        slotProps={{ toolbar: { actions: toolbarActions } }}
                        showToolbar
                        sx={{ '--DataGrid-overlayHeight': '300px' }}
                        localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                        rowHeight={80}
                        className={styles.DataGrid}
                        initialState={{
                            filter: {
                                filterModel: {
                                    items: [],
                                    quickFilterExcludeHiddenColumns: false
                                }
                            }
                        }}
                        columnVisibilityModel={{
                            childsName: false
                        }}
                    />
                </Mobile>
            </div>
            <ChildListDialog
                isDialogOpen={isChildDialogOpen}
                handleCloseDialog={() => setChildDialogOpen(false)}
                childs={childs}
                currentDaycareId={currentDaycare?.id || 0}
                isRpeOrLaep={isRpeOrLaep}
            />
            {currentDaycare && user?.currentDaycareRights.canWriteFamilies && (
                <BulkDissociateParentsDialog
                    isDialogOpen={isBulkDissociateDialogOpen}
                    handleCloseDialog={() => setBulkDissociateDialogOpen(false)}
                    selectedParentIds={selectedParentIds}
                    daycareId={currentDaycare.id}
                    onSuccess={() => {
                        setSelectedParentIds([])
                        refetchAllParent()
                    }}
                />
            )}
        </>
    )
}

export default FamiliesTableHandler
